import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Actions the mobile bottom bar shows while an option-group detail (Tier 2) is open, so it can
 * "switch" from the room-level Back/Complete actions to the in-detail Clear selection / Accept
 * controls. Registered by `GroupOptionDetail`, consumed by `MobileConfiguratorActions`.
 */
export interface OptionDetailActions {
    /** The group currently has a selected option (enables Accept; toggles Clear vs Close). */
    hasSelection: boolean;
    /** Clear the group's selection (stays in the detail view). */
    onClear: () => void;
    /** Leave the detail view (Accept when selected, Close when not) — both return to the group list. */
    onClose: () => void;
}

interface MobileActionsContextValue {
    detail: OptionDetailActions | null;
    setDetail: (detail: OptionDetailActions | null) => void;
}

const MobileActionsContext = createContext<MobileActionsContextValue | undefined>(undefined);

/**
 * Bridges the option-group detail view (deep inside the scrolling Room) and the shell-level mobile
 * bottom bar (a sibling of Room). Wrap both in the compact shells so the bar can reflect the detail.
 */
export const MobileActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [detail, setDetail] = useState<OptionDetailActions | null>(null);
    const value = useMemo(() => ({ detail, setDetail }), [detail]);
    return <MobileActionsContext.Provider value={value}>{children}</MobileActionsContext.Provider>;
};

/** Returns the context, or undefined when rendered outside a provider (e.g. auth accordion). */
export const useMobileActions = () => useContext(MobileActionsContext);
